import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { AlertCircle, CheckCircle, Clock, Save, RotateCcw, X, Settings, Image, Zap, RefreshCw, History, List } from 'lucide-react';
import { LogoUpload } from './LogoUpload';
import { HoursChangelogModal } from './HoursChangelogModal';
import {
  getHoursBankStatus,
  updateProjectSettings,
  getHoursBank,
  configureHoursBank,
  getProject,
  configureJiraIntegration,
  removeJiraIntegration,
  testJiraConnection,
  getHoursDefaults,
  setHoursDefaults,
  getJiraFields,
  discoverJiraStoryPoints,
  listJiraProjects
} from '../../services/api';
import { HoursBankAreaData } from '../../types/hoursBank';
import { usePermissions } from '../../contexts/PermissionsContext';

interface ProjectSettingsProps {
  projectId: string;
  projectName: string;
  isOpen: boolean;
  onClose: () => void;
}

interface HoursBankData {
  frontend?: HoursBankAreaData;
  backend?: HoursBankAreaData;
  design?: HoursBankAreaData;
  'Q&A'?: HoursBankAreaData;
}

type TabType = 'hours' | 'logo' | 'jira';

export const ProjectSettings: React.FC<ProjectSettingsProps> = ({
  projectId,
  projectName,
  isOpen,
  onClose
}) => {
  const { canManage } = usePermissions();
  const canManageSettings = canManage('settings');

  const [activeTab, setActiveTab] = useState<TabType>('hours');
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState<any>(null);
  
  // Hours Bank State
  const [isEnabled, setIsEnabled] = useState(false);
  const [currentDate, setCurrentDate] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [hoursBankData, setHoursBankData] = useState<HoursBankData>({});
  const [hourInputs, setHourInputs] = useState({
    frontend: 0,
    backend: 0,
    design: 0,
    'Q&A': 0
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Default Hours State
  const [defaultHours, setDefaultHours] = useState({
    frontend: 0,
    backend: 0,
    design: 0,
    'Q&A': 0
  });
  const [hasDefaults, setHasDefaults] = useState(false);
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [defaultsReason, setDefaultsReason] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  // JIRA State
  const [jiraConfig, setJiraConfig] = useState({
    projectKey: '',
    jiraUrl: '',
    username: '',
    apiToken: ''
  });
  const [jiraEnabled, setJiraEnabled] = useState(false);
  const [jiraLoading, setJiraLoading] = useState(false);
  const [jiraTestSuccess, setJiraTestSuccess] = useState(false);

  // Story Points State
  const [useStoryPoints, setUseStoryPoints] = useState(true);
  const [useTimeTracking, setUseTimeTracking] = useState(true);
  const [hoursToPointsRatio, setHoursToPointsRatio] = useState(1.0);
  const [storyPointsFieldId, setStoryPointsFieldId] = useState<string | null>(null);
  const [discoveringStoryPoints, setDiscoveringStoryPoints] = useState(false);
  const [storyPointsDiscoverySuccess, setStoryPointsDiscoverySuccess] = useState(false);
  const [jiraTestError, setJiraTestError] = useState<string | null>(null);
  const [jiraSaveSuccess, setJiraSaveSuccess] = useState(false);
  const [hasExistingToken, setHasExistingToken] = useState(false);

  // Project Listing State
  const [showProjectList, setShowProjectList] = useState(false);
  const [availableProjects, setAvailableProjects] = useState<Array<{ key: string; name: string; projectTypeKey: string; lead?: string }>>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadProjectData();
      loadHoursBankStatus();
      loadHoursDefaults();
    }
  }, [isOpen, projectId]);

  // Effect to reload hours bank data when month/year changes
  useEffect(() => {
    if (isOpen && isEnabled) {
      loadHoursBankData();
    }
  }, [currentDate.year, currentDate.month, isOpen, isEnabled]);

  const loadProjectData = async () => {
    try {
      const response = await getProject(projectId);
      const projectData = response.data.data.project;
      setProject(projectData);
      
      // Load JIRA configuration
      if (projectData.jiraEnabled && projectData.jiraConfig) {
        setJiraEnabled(true);
        setHasExistingToken(true); // Indica que já existe um token configurado
        setJiraConfig({
          projectKey: projectData.jiraConfig.projectKey || '',
          jiraUrl: projectData.jiraConfig.jiraUrl || '',
          username: projectData.jiraConfig.username || '',
          apiToken: '' // Don't load the token for security
        });

        // Load Story Points configuration
        setUseStoryPoints(projectData.jiraConfig.useStoryPoints !== false);
        setUseTimeTracking(projectData.jiraConfig.useTimeTracking !== false);
        setHoursToPointsRatio(projectData.jiraConfig.hoursToPointsRatio || 1.0);
        setStoryPointsFieldId(projectData.jiraConfig.storyPointsFieldId || null);
      } else {
        setHasExistingToken(false);
      }
    } catch (error) {
      console.error('Error loading project:', error);
    }
  };

  const loadHoursBankStatus = async () => {
    try {
      setLoading(true);
      const response = await getHoursBankStatus(projectId);
      const { hoursTrackingEnabled: enabled } = response.data.data;
      setIsEnabled(enabled);
      
      if (enabled) {
        await loadHoursBankData();
      }
    } catch (error) {
      console.error('Error loading hours bank status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadHoursBankData = async () => {
    try {
      setLoading(true);
      const response = await getHoursBank(projectId, currentDate.year, currentDate.month);
      const data = response.data.data;
      setHoursBankData(data.hoursBank || {});
      
      // Set input values based on actual API response structure
      const newInputs = { frontend: 0, backend: 0, design: 0, 'Q&A': 0 };
      Object.entries(data.hoursBank || {}).forEach(([area, hoursBankObj]: [string, any]) => {
        if (hoursBankObj && hoursBankObj.allocatedHours) {
          newInputs[area as keyof typeof newInputs] = hoursBankObj.allocatedHours;
        }
      });
      setHourInputs(newInputs);
    } catch (error) {
      console.error('Error loading hours bank data:', error);
      setHoursBankData({});
      setHourInputs({ frontend: 0, backend: 0, design: 0, 'Q&A': 0 });
    } finally {
      setLoading(false);
    }
  };

  const loadHoursDefaults = async () => {
    try {
      const response = await getHoursDefaults(projectId);
      if (response.data.data) {
        const defaults = response.data.data.defaultHours;
        setDefaultHours(defaults);
        setHasDefaults(true);
      } else {
        setHasDefaults(false);
      }
    } catch (error) {
      console.error('Error loading hours defaults:', error);
      setHasDefaults(false);
    }
  };

  const handleToggleHoursBank = async (enabled: boolean) => {
    try {
      setLoading(true);
      await updateProjectSettings(projectId, { hoursTrackingEnabled: enabled });
      setIsEnabled(enabled);
      
      if (enabled) {
        await loadHoursBankData();
      } else {
        setHoursBankData({});
        setHourInputs({ frontend: 0, backend: 0, design: 0, 'Q&A': 0 });
      }
    } catch (error) {
      console.error('Error toggling hours bank:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHourChange = (area: string, value: string) => {
    const numValue = value === '' ? 0 : Math.max(0, parseFloat(value) || 0);
    setHourInputs(prev => ({ ...prev, [area]: numValue }));
  };

  const saveHoursConfiguration = async () => {
    try {
      setLoading(true);
      
      const configData = Object.entries(hourInputs)
        .filter(([_, hours]) => hours > 0)
        .reduce((acc, [area, hours]) => {
          acc[area as keyof typeof hourInputs] = hours;
          return acc;
        }, {} as { frontend?: number; backend?: number; design?: number; 'Q&A'?: number; });

      if (Object.keys(configData).length === 0) {
        console.warn('No hours configured for any area');
        return;
      }

      // Check if values are different from defaults
      const isDifferentFromDefaults = hasDefaults && Object.keys(configData).some(
        area => configData[area as keyof typeof configData] !== defaultHours[area as keyof typeof defaultHours]
      );
      
      // Include reason if it's an override
      const reason = isDifferentFromDefaults ? overrideReason : undefined;
      
      await configureHoursBank(projectId, currentDate.year, currentDate.month, configData, reason);
      await loadHoursBankData();
      setOverrideReason('');
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving hours configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDefaults = async () => {
    try {
      setLoading(true);
      await setHoursDefaults(projectId, defaultHours, defaultsReason);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      setDefaultsReason('');
      setHasDefaults(true);
      
      // Reload data
      await loadHoursDefaults();
    } catch (error) {
      console.error('Error saving defaults:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUseDefaults = () => {
    if (hasDefaults) {
      setHourInputs(defaultHours);
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      let newMonth = prev.month;
      let newYear = prev.year;

      if (direction === 'next') {
        newMonth = prev.month === 12 ? 1 : prev.month + 1;
        newYear = prev.month === 12 ? prev.year + 1 : prev.year;
      } else {
        newMonth = prev.month === 1 ? 12 : prev.month - 1;
        newYear = prev.month === 1 ? prev.year - 1 : prev.year;
      }

      return { year: newYear, month: newMonth };
    });
  };

  const handleLogoChange = (logoPath: string | null) => {
    setProject((prev: any) => ({
      ...prev,
      logo: logoPath
    }));
  };

  // JIRA Functions
  const handleJiraConfigChange = (field: string, value: string) => {
    setJiraConfig(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Se o usuário digitou um novo token, marca que não é mais apenas o existente
    if (field === 'apiToken' && value.trim()) {
      setHasExistingToken(false);
    }
    
    // Clear test results when config changes
    setJiraTestSuccess(false);
    setJiraTestError(null);
  };

  const handleTestJiraConnection = async () => {
    // Valida se os campos obrigatórios estão preenchidos (considera token existente)
    if (!jiraConfig.projectKey || !jiraConfig.jiraUrl || !jiraConfig.username || (!jiraConfig.apiToken && !hasExistingToken)) {
      setJiraTestError('Todos os campos são obrigatórios para testar a conexão');
      return;
    }

    try {
      setJiraLoading(true);
      setJiraTestError(null);
      setJiraTestSuccess(false);

      // First save the configuration
      await configureJiraIntegration(projectId, jiraConfig);
      
      // Then test the connection
      await testJiraConnection(projectId);
      
      setJiraTestSuccess(true);
      setTimeout(() => setJiraTestSuccess(false), 5000);
    } catch (error: any) {
      console.error('JIRA test error:', error);
      console.error('Error details:', {
        response: error.response,
        data: error.response?.data,
        message: error.response?.data?.message,
        status: error.response?.status
      });

      // Tratamento específico para token corrompido
      if (error.response?.data?.code === 'CORRUPTED_TOKEN') {
        const errorMsg = 'Token JIRA corrompido. Configure um novo token para continuar.';
        console.log('Setting error:', errorMsg);
        setJiraTestError(errorMsg);
        // Força o usuário a reconfigurar o token
        setHasExistingToken(false);
        setJiraConfig(prev => ({ ...prev, apiToken: '' }));
      } else {
        const errorMsg = error.response?.data?.message || 'Erro ao testar conexão JIRA';
        console.log('Setting error:', errorMsg);
        setJiraTestError(errorMsg);
      }
    } finally {
      setJiraLoading(false);
    }
  };

  const handleSaveJiraConfig = async () => {
    // Valida se os campos obrigatórios estão preenchidos (considera token existente)
    if (!jiraConfig.projectKey || !jiraConfig.jiraUrl || !jiraConfig.username || (!jiraConfig.apiToken && !hasExistingToken)) {
      setJiraTestError('Todos os campos são obrigatórios');
      return;
    }

    try {
      setJiraLoading(true);
      setJiraTestError(null);

      // Include Story Points configuration
      const configWithStoryPoints = {
        ...jiraConfig,
        useStoryPoints,
        useTimeTracking,
        hoursToPointsRatio,
        storyPointsFieldId: storyPointsFieldId || undefined
      };

      await configureJiraIntegration(projectId, configWithStoryPoints);

      setJiraEnabled(true);
      setJiraSaveSuccess(true);
      setTimeout(() => setJiraSaveSuccess(false), 3000);

      // Reload project data
      await loadProjectData();
    } catch (error: any) {
      console.error('JIRA save error:', error);

      // Tratamento específico para erro de formato de token
      if (error.response?.status === 400 && error.response?.data?.message?.includes('Token JIRA inválido')) {
        setJiraTestError(`Formato de token inválido: ${error.response.data.message.split(':')[1]?.trim() || 'Verifique se copiou corretamente'}`);
      } else {
        setJiraTestError(error.response?.data?.message || 'Erro ao salvar configuração JIRA');
      }
    } finally {
      setJiraLoading(false);
    }
  };

  const handleRemoveJiraConfig = async () => {
    if (!window.confirm('Tem certeza que deseja remover a integração JIRA?')) {
      return;
    }

    try {
      setJiraLoading(true);
      setJiraTestError(null);

      await removeJiraIntegration(projectId);
      
      setJiraEnabled(false);
      setJiraConfig({
        projectKey: '',
        jiraUrl: '',
        username: '',
        apiToken: ''
      });
      setHasExistingToken(false);
      
      // Reload project data
      await loadProjectData();
    } catch (error: any) {
      setJiraTestError(error.response?.data?.message || 'Erro ao remover configuração JIRA');
      console.error('JIRA remove error:', error);
    } finally {
      setJiraLoading(false);
    }
  };

  const handleDiscoverStoryPoints = async () => {
    try {
      setDiscoveringStoryPoints(true);
      setJiraTestError(null);
      setStoryPointsDiscoverySuccess(false);

      const response = await discoverJiraStoryPoints(projectId);

      if (response.data.success) {
        setStoryPointsFieldId(response.data.data.fieldId);
        setStoryPointsDiscoverySuccess(true);
        setTimeout(() => setStoryPointsDiscoverySuccess(false), 3000);
      }
    } catch (error: any) {
      setJiraTestError(error.response?.data?.message || 'Erro ao descobrir campo Story Points');
      console.error('Story Points discovery error:', error);
    } finally {
      setDiscoveringStoryPoints(false);
    }
  };

  const handleListJiraProjects = async () => {
    // Validação: campos obrigatórios devem estar preenchidos
    if (!jiraConfig.jiraUrl || !jiraConfig.username || (!jiraConfig.apiToken && !hasExistingToken)) {
      setJiraTestError('Preencha URL, Email e API Token antes de listar projetos');
      return;
    }

    try {
      setLoadingProjects(true);
      setJiraTestError(null);

      console.log('🔄 Salvando credenciais JIRA antes de listar projetos...');

      // 1. Salvar credenciais primeiro
      const configWithStoryPoints = {
        ...jiraConfig,
        useStoryPoints,
        useTimeTracking,
        hoursToPointsRatio,
        storyPointsFieldId: storyPointsFieldId || undefined
      };

      await configureJiraIntegration(projectId, configWithStoryPoints);

      console.log('✅ Credenciais salvas com sucesso');
      setJiraEnabled(true);
      setHasExistingToken(true);

      // Recarrega dados do projeto para garantir que tudo está sincronizado
      await loadProjectData();

      console.log('🔍 Listando projetos disponíveis...');

      // 2. Listar projetos
      const response = await listJiraProjects(projectId);

      if (response.data.success) {
        const projects = response.data.data.projects;
        console.log(`✅ ${projects.length} projetos encontrados`);

        setAvailableProjects(projects);
        setShowProjectList(true);

        if (projects.length === 0) {
          setJiraTestError('Nenhum projeto encontrado. Verifique se a conta tem acesso a projetos no Jira.');
        }
      }
    } catch (error: any) {
      console.error('❌ Erro ao salvar credenciais ou listar projetos:', error);

      // Mensagens de erro mais específicas
      if (error.response?.status === 401 || error.response?.data?.message?.includes('Credenciais')) {
        setJiraTestError('Credenciais inválidas. Verifique o email e o API Token.');
      } else if (error.response?.status === 400 && error.response?.data?.message?.includes('Token JIRA inválido')) {
        setJiraTestError(`Formato de token inválido: ${error.response.data.message.split(':')[1]?.trim() || 'Verifique se copiou corretamente'}`);
      } else {
        setJiraTestError(error.response?.data?.message || 'Erro ao salvar credenciais ou listar projetos JIRA');
      }
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleSelectProject = (projectKey: string) => {
    setJiraConfig(prev => ({
      ...prev,
      projectKey
    }));
    setShowProjectList(false);
    setJiraTestError(null);
  };

  const areas = [
    { key: 'frontend', label: 'Frontend', color: 'from-blue-500 to-blue-600' },
    { key: 'backend', label: 'Backend', color: 'from-green-500 to-green-600' },
    { key: 'design', label: 'Design', color: 'from-purple-500 to-purple-600' },
    { key: 'Q&A', label: 'Q&A', color: 'from-orange-500 to-orange-600' }
  ] as const;

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const tabs = [
    { id: 'hours', label: 'Banco de Horas', icon: Clock },
    { id: 'logo', label: 'Logo Institucional', icon: Image },
    { id: 'jira', label: 'Integração JIRA', icon: Zap }
  ] as const;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card variant="glass" className="w-full max-w-5xl max-h-[90vh] bg-background-secondary border border-white/10 overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between pb-4 border-b border-white/10">
          <div>
            <CardTitle className="text-xl font-heading text-foreground-primary flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Configurações do Projeto
            </CardTitle>
            <p className="text-foreground-tertiary mt-1">
              {projectName}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0 border-white/20 hover:bg-white/10"
          >
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        {/* Tabs Navigation */}
        <div className="flex border-b border-white/10">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`px-6 py-4 text-sm font-medium relative flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'text-blue-400 bg-blue-500/10'
                    : 'text-foreground-secondary hover:text-foreground-primary hover:bg-white/5'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <CardContent className="p-6">
            {/* Hours Bank Tab */}
            {activeTab === 'hours' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground-primary">Banco de Horas</h3>
                    <p className="text-sm text-foreground-tertiary">
                      Configure o rastreamento de horas por área de desenvolvimento
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-foreground-secondary">
                      {isEnabled ? 'Habilitado' : 'Desabilitado'}
                    </span>
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={handleToggleHoursBank}
                      disabled={loading || !canManageSettings}
                    />
                  </div>
                </div>

                {isEnabled && (
                  <div className="space-y-6">
                    {/* Default Hours Configuration */}
                    <div className="border border-white/10 rounded-lg p-4 bg-white/5">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-md font-medium text-foreground-primary">
                          Configuração Padrão de Horas
                        </h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowChangelogModal(true)}
                          className="border-white/20 hover:bg-white/10"
                        >
                          <History className="w-4 h-4 mr-2" />
                          Histórico
                        </Button>
                      </div>
                      
                      <p className="text-sm text-foreground-tertiary mb-4">
                        Defina as horas padrão que serão aplicadas automaticamente todo mês
                      </p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        {areas.map((area) => (
                          <div key={area.key}>
                            <label className="text-xs font-medium text-foreground-secondary">
                              {area.label}
                            </label>
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              value={defaultHours[area.key] || ''}
                              onChange={(e) => setDefaultHours(prev => ({
                                ...prev,
                                [area.key]: parseFloat(e.target.value) || 0
                              }))}
                              className="mt-1 h-9"
                              placeholder="0"
                            />
                          </div>
                        ))}
                      </div>
                      
                      {!hasDefaults && (
                        <Input
                          type="text"
                          value={defaultsReason}
                          onChange={(e) => setDefaultsReason(e.target.value)}
                          placeholder="Motivo da configuração (opcional)"
                          className="mb-3"
                        />
                      )}
                      
                      <Button
                        onClick={handleSaveDefaults}
                        disabled={loading || !canManageSettings}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {hasDefaults ? 'Atualizar Padrão' : 'Salvar como Padrão'}
                      </Button>
                      
                      {hasDefaults && (
                        <div className="mt-3 text-xs text-green-400 flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Configuração padrão definida
                        </div>
                      )}
                    </div>

                    {/* Monthly Configuration */}
                    <div className="flex items-center justify-between">
                      <h4 className="text-md font-medium text-foreground-primary flex items-center gap-2">
                        Configuração Mensal
                        {hoursBankData && Object.values(hoursBankData).some((hb: any) => hb?.isDefault) && (
                          <span className="text-xs text-blue-400 flex items-center">
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Usando padrão
                          </span>
                        )}
                        {hoursBankData && Object.values(hoursBankData).some((hb: any) => hb?.isOverride) && (
                          <span className="text-xs text-yellow-400 flex items-center">
                            ✏️ Override ativo
                          </span>
                        )}
                      </h4>
                      <div className="flex items-center gap-2">
                        {hasDefaults && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleUseDefaults}
                            className="border-white/20 hover:bg-white/10"
                          >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Usar Padrão
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigateMonth('prev')}
                          className="h-8 w-8 p-0 border-white/20 hover:bg-white/10"
                        >
                          ←
                        </Button>
                        <span className="text-sm font-medium text-foreground-primary px-3">
                          {monthNames[currentDate.month - 1]} {currentDate.year}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigateMonth('next')}
                          className="h-8 w-8 p-0 border-white/20 hover:bg-white/10"
                        >
                          →
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {areas.map((area) => {
                        const areaData = hoursBankData[area.key as keyof HoursBankData];
                        const currentHours = hourInputs[area.key] || 0;
                        const usedHours = areaData?.usedHours || 0;
                        const availableHours = Math.max(0, currentHours - usedHours);
                        const isUsingDefault = areaData?.isDefault;
                        const isOverride = areaData?.isOverride;

                        return (
                          <Card key={area.key} variant="glass">
                            <CardHeader>
                              <CardTitle className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${area.color}`} />
                                  {area.label}
                                </div>
                                {isUsingDefault && (
                                  <span className="text-xs text-blue-400" title="Usando valor padrão">
                                    <RefreshCw className="w-3 h-3" />
                                  </span>
                                )}
                                {isOverride && (
                                  <span className="text-xs text-yellow-400" title="Override específico do mês">
                                    ✏️
                                  </span>
                                )}
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                              <div>
                                <label className="text-sm font-medium text-foreground-secondary">
                                  Horas Alocadas
                                </label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  value={hourInputs[area.key] || ''}
                                  onChange={(e) => handleHourChange(area.key, e.target.value)}
                                  className="mt-1"
                                  placeholder="0"
                                />
                              </div>

                              {areaData && (
                                <div className="text-xs text-foreground-tertiary space-y-1">
                                  <div className="flex justify-between">
                                    <span>Usado:</span>
                                    <span>{usedHours}h</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Disponível:</span>
                                    <span className={availableHours > 0 ? 'text-green-400' : 'text-red-400'}>
                                      {availableHours}h
                                    </span>
                                  </div>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>

                    {/* Override Reason Field */}
                    {hasDefaults && hourInputs && Object.keys(hourInputs).some(
                      area => hourInputs[area as keyof typeof hourInputs] !== defaultHours[area as keyof typeof defaultHours]
                    ) && (
                      <div>
                        <label className="text-sm font-medium text-foreground-secondary">
                          Motivo da alteração (override)
                        </label>
                        <Input
                          type="text"
                          value={overrideReason}
                          onChange={(e) => setOverrideReason(e.target.value)}
                          placeholder="Explique porque está diferente do padrão (opcional)"
                          className="mt-1"
                        />
                      </div>
                    )}

                    <div className="flex items-center gap-3 pt-4">
                      <Button
                        onClick={saveHoursConfiguration}
                        disabled={loading || !canManageSettings}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {loading ? (
                          <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Salvar Configuração
                      </Button>

                      {saveSuccess && (
                        <div className="flex items-center text-green-400 text-sm">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Configuração salva!
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Logo Tab */}
            {activeTab === 'logo' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground-primary">Logo Institucional</h3>
                  <p className="text-sm text-foreground-tertiary">
                    Configure o logo da sua empresa para apresentações profissionais das ordens de serviço
                  </p>
                </div>

                <LogoUpload
                  projectId={projectId}
                  projectName={projectName}
                  currentLogo={project?.logo}
                  onLogoChange={handleLogoChange}
                />
              </div>
            )}

            {/* JIRA Tab */}
            {activeTab === 'jira' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground-primary">Integração JIRA</h3>
                    <p className="text-sm text-foreground-tertiary">
                      Configure a integração com JIRA para exportar tarefas e ordens de serviço
                    </p>
                  </div>
                  
                  {jiraEnabled && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-green-400">✓ Configurado</span>
                      {canManageSettings && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveJiraConfig}
                          disabled={jiraLoading}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          Remover
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground-primary block mb-2">
                      URL do JIRA *
                    </label>
                    <Input
                      type="url"
                      placeholder="https://sua-empresa.atlassian.net"
                      value={jiraConfig.jiraUrl}
                      onChange={(e) => handleJiraConfigChange('jiraUrl', e.target.value)}
                      disabled={jiraLoading || !canManageSettings}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-foreground-primary block mb-2">
                      Email/Username *
                    </label>
                    <Input
                      type="text"
                      placeholder="seu-email@empresa.com"
                      value={jiraConfig.username}
                      onChange={(e) => handleJiraConfigChange('username', e.target.value)}
                      disabled={jiraLoading || !canManageSettings}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="text-sm font-medium text-foreground-primary block mb-2">
                      API Token *
                      {hasExistingToken && !jiraConfig.apiToken && (
                        <span className="text-xs text-green-400 ml-2">✓ Configurado</span>
                      )}
                    </label>
                    <Input
                      type="password"
                      placeholder={hasExistingToken && !jiraConfig.apiToken ?
                        "••••••••••••••••••••••• (Token já configurado - deixe vazio para manter)" :
                        "Seu API Token do JIRA"
                      }
                      value={jiraConfig.apiToken}
                      onChange={(e) => handleJiraConfigChange('apiToken', e.target.value)}
                      disabled={jiraLoading || !canManageSettings}
                    />
                    {hasExistingToken && !jiraConfig.apiToken && (
                      <p className="text-xs text-foreground-tertiary mt-1">
                        Um token já está configurado. Deixe vazio para manter o atual ou digite um novo para substituir.
                      </p>
                    )}
                    {!hasExistingToken && (
                      <p className="text-xs text-foreground-tertiary mt-1">
                        O token deve começar com "ATATT" e ter pelo menos 40 caracteres.
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-blue-400 mb-2">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Como obter o API Token</span>
                  </div>
                  <p className="text-sm text-blue-300">
                    1. Acesse <strong>https://id.atlassian.com/manage-profile/security/api-tokens</strong>
                    <br />
                    2. Clique em "Create API token"
                    <br />
                    3. Dê um nome ao token e clique em "Create"
                    <br />
                    4. Copie o token gerado e cole no campo acima
                  </p>
                </div>

                {/* Separador */}
                <div className="border-t border-white/10"></div>

                <div>
                  <h4 className="text-md font-medium text-foreground-primary mb-2">Selecione o Projeto</h4>
                  <label className="text-sm font-medium text-foreground-secondary block mb-2">
                    Chave do Projeto *
                  </label>
                  <p className="text-xs text-foreground-tertiary mb-2">
                    💡 Não sabe a chave? Use o botão "Listar Projetos" para ver todos os seus projetos disponíveis
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="PROJ"
                      value={jiraConfig.projectKey}
                      onChange={(e) => handleJiraConfigChange('projectKey', e.target.value)}
                      disabled={jiraLoading || loadingProjects || !canManageSettings}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleListJiraProjects}
                      disabled={loadingProjects || jiraLoading || !jiraConfig.jiraUrl || !jiraConfig.username || (!jiraConfig.apiToken && !hasExistingToken) || !canManageSettings}
                      className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 whitespace-nowrap"
                      title="Salva as credenciais e lista todos os projetos disponíveis"
                    >
                      {loadingProjects ? (
                        <>
                          <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
                          Listando...
                        </>
                      ) : (
                        <>
                          <List className="w-4 h-4 mr-2" />
                          Listar Projetos
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Project List */}
                  {showProjectList && availableProjects.length > 0 && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 text-blue-400">
                          <List className="w-4 h-4" />
                          <span className="text-sm font-medium">Projetos Disponíveis ({availableProjects.length})</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowProjectList(false)}
                          className="h-6 w-6 p-0 border-blue-500/30 hover:bg-blue-500/10"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>

                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {availableProjects.map((project) => (
                          <button
                            key={project.key}
                            onClick={() => handleSelectProject(project.key)}
                            className="group w-full text-left p-3 rounded-lg bg-blue-500/5 hover:bg-blue-500/10 border border-blue-500/20 hover:border-blue-500/40 transition-all"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-blue-300">{project.key}</span>
                                  <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-300">
                                    {project.projectTypeKey}
                                  </span>
                                </div>
                                <p className="text-sm text-foreground-secondary mt-1">{project.name}</p>
                                {project.lead && (
                                  <p className="text-xs text-foreground-tertiary mt-1">
                                    Lead: {project.lead}
                                  </p>
                                )}
                              </div>
                              <CheckCircle className="w-4 h-4 text-blue-400 opacity-0 group-hover:opacity-100" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Story Points Configuration */}
                {jiraEnabled && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 space-y-4">
                    <div className="flex items-center gap-2 text-purple-400 mb-2">
                      <Settings className="w-4 h-4" />
                      <span className="text-sm font-medium">Configuração de Estimativas</span>
                    </div>

                    <div className="space-y-3">
                      {/* Use Story Points Toggle */}
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-foreground-primary">Usar Story Points</label>
                          <p className="text-xs text-foreground-tertiary mt-1">
                            Adiciona Story Points calculados automaticamente a partir das horas estimadas
                          </p>
                        </div>
                        <Switch
                          checked={useStoryPoints}
                          onCheckedChange={setUseStoryPoints}
                          disabled={jiraLoading || !canManageSettings}
                        />
                      </div>

                      {/* Story Points Field Discovery */}
                      {useStoryPoints && (
                        <div className="space-y-2 pl-4 border-l-2 border-purple-500/30">
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={handleDiscoverStoryPoints}
                              disabled={discoveringStoryPoints || jiraLoading || !canManageSettings}
                              size="sm"
                              variant="outline"
                              className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                            >
                              {discoveringStoryPoints ? (
                                <RotateCcw className="w-3 h-3 mr-2 animate-spin" />
                              ) : (
                                <Zap className="w-3 h-3 mr-2" />
                              )}
                              Descobrir Campo Story Points
                            </Button>

                            {storyPointsFieldId && (
                              <span className="text-xs text-green-400 flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                {storyPointsFieldId}
                              </span>
                            )}
                          </div>

                          {storyPointsDiscoverySuccess && (
                            <p className="text-xs text-green-400">
                              Campo Story Points descoberto e cacheado com sucesso!
                            </p>
                          )}

                          {/* Hours to Points Ratio */}
                          <div>
                            <label className="text-sm font-medium text-foreground-primary block mb-2">
                              Conversão (horas por ponto)
                            </label>
                            <Input
                              type="number"
                              step="0.5"
                              min="0.1"
                              max="100"
                              value={hoursToPointsRatio}
                              onChange={(e) => setHoursToPointsRatio(Number(e.target.value))}
                              disabled={jiraLoading || !canManageSettings}
                              className="w-32"
                            />
                            <p className="text-xs text-foreground-tertiary mt-1">
                              Exemplo: 8 horas = 1 Story Point
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Use Time Tracking Toggle */}
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-foreground-primary">Usar Time Tracking</label>
                          <p className="text-xs text-foreground-tertiary mt-1">
                            Adiciona estimativa no campo Time Tracking do Jira (se disponível)
                          </p>
                        </div>
                        <Switch
                          checked={useTimeTracking}
                          onCheckedChange={setUseTimeTracking}
                          disabled={jiraLoading || !canManageSettings}
                        />
                      </div>

                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                        <p className="text-xs text-blue-300">
                          💡 <strong>Dica:</strong> Story Points funcionam melhor que Time Tracking em projetos Next-Gen/Team-Managed.
                          Você pode usar ambos simultaneamente se desejar.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {canManageSettings && (
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleTestJiraConnection}
                      disabled={jiraLoading || !jiraConfig.projectKey || !jiraConfig.jiraUrl || !jiraConfig.username || (!jiraConfig.apiToken && !hasExistingToken)}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {jiraLoading ? (
                        <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Zap className="w-4 h-4 mr-2" />
                      )}
                      Testar Conexão
                    </Button>

                    <Button
                      onClick={handleSaveJiraConfig}
                      disabled={jiraLoading || !jiraConfig.projectKey || !jiraConfig.jiraUrl || !jiraConfig.username || (!jiraConfig.apiToken && !hasExistingToken)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {jiraLoading ? (
                        <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Salvar Configuração
                    </Button>
                  </div>
                )}

                {/* Success Messages */}
                {jiraTestSuccess && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Conexão testada com sucesso!</span>
                    </div>
                  </div>
                )}

                {jiraSaveSuccess && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Configuração salva com sucesso!</span>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {jiraTestError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 animate-pulse-once">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-red-400 mb-1">❌ Erro na Conexão</p>
                        <p className="text-sm text-red-300 whitespace-pre-line">{jiraTestError}</p>

                        {/* Helpful tips based on error */}
                        {(jiraTestError.includes('404') || jiraTestError.includes('não encontrado')) && (
                          <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                            <p className="text-xs text-blue-300">
                              💡 <strong>Dica:</strong> Verifique se a chave do projeto está correta.
                              Ela geralmente aparece na URL do Jira (ex: <code className="bg-blue-500/20 px-1 rounded">PROJ</code> em https://empresa.atlassian.net/browse/<code className="bg-blue-500/20 px-1 rounded">PROJ</code>-123)
                            </p>
                          </div>
                        )}

                        {(jiraTestError.includes('401') || jiraTestError.includes('Credenciais')) && (
                          <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                            <p className="text-xs text-blue-300">
                              💡 <strong>Dica:</strong> Verifique se o email e o API Token estão corretos.
                              O token deve começar com <code className="bg-blue-500/20 px-1 rounded">ATATT</code>.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </div>
      </Card>
      
      {/* Changelog Modal */}
      <HoursChangelogModal
        projectId={projectId}
        isOpen={showChangelogModal}
        onClose={() => setShowChangelogModal(false)}
      />
    </div>
  );
};

export default ProjectSettings;